import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, CheckCircle2, MapPin, Phone, Mail, Instagram } from "lucide-react";
import PageHero from "@/components/aevos/PageHero";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { pushGtmEvent } from "@/lib/gtm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = ["Consulta Inicial", "Plano Start", "Plano Performance", "Plano Elite", "AEVOS 365", "Tecnologia / Exames", "Outro"];

const ContactWhatsAppCard = () => (
  <div className="aevos-card p-7">
    <p className="aevos-eyebrow mb-3">WhatsApp</p>
    <h3 className="font-display font-bold text-xl text-black">Fale agora com a clínica.</h3>
    <p className="text-sm text-[#333] mt-3">Resposta rápida em horário comercial.</p>
    <a
      href="https://wa.me/5511941546532?text=Olá! Vim pelo site AEVOS e gostaria de agendar."
      target="_blank" rel="noreferrer"
      data-testid="contato-whatsapp-cta"
      onClick={() => pushGtmEvent("whatsapp_click", { location: "contato_page" })}
    >
      <span className="btn-cyan mt-5">(11) 94154-6532 <ArrowRight size={16}/></span>
    </a>
  </div>
);

const ContactAddressCard = () => (
  <div className="aevos-card p-7">
    <p className="aevos-eyebrow mb-3">Onde estamos</p>
    <ul className="space-y-3 text-sm text-[#333]">
      <li className="flex items-start gap-2"><MapPin size={16} className="text-[#00C4FF] mt-0.5"/> R. Waldemar Zierold, 23 — Vila Sarah Avignon, Mogi das Cruzes/SP · CEP 08773-470</li>
      <li className="flex items-start gap-2"><Phone size={16} className="text-[#00C4FF] mt-0.5"/> (11) 94154-6532</li>
      <li className="flex items-start gap-2"><Mail size={16} className="text-[#00C4FF] mt-0.5"/> contato@aevos.com.br</li>
      <li className="flex items-start gap-2"><Instagram size={16} className="text-[#00C4FF] mt-0.5"/> @drguilherme.affonso</li>
    </ul>
  </div>
);

const ContactSuccessCard = ({ firstName }) => (
  <div className="aevos-card p-10 text-center" data-testid="contato-success">
    <div className="w-14 h-14 rounded-full bg-[#E6F8FF] border border-[#00C4FF] inline-flex items-center justify-center">
      <CheckCircle2 size={24} className="text-[#00C4FF]"/>
    </div>
    <h3 className="font-display font-bold text-2xl text-black mt-5">Mensagem recebida.</h3>
    <p className="mt-3 text-[#333] max-w-md mx-auto">
      Obrigado, {firstName}. A equipe AEVOS vai entrar em contato em breve pelo WhatsApp informado.
    </p>
  </div>
);

const ContactFormCard = ({ form, setForm, loading, onSubmit }) => (
  <form onSubmit={onSubmit} className="aevos-card p-8" data-testid="contato-form">
    <div className="aevos-eyebrow mb-3">Formulário de contato</div>
    <h3 className="font-display font-bold text-2xl text-black">Diga como podemos te atender.</h3>

    <div className="mt-6 grid md:grid-cols-2 gap-4">
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">Nome</Label>
        <Input data-testid="contato-input-name" required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="mt-1"/>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">E-mail</Label>
        <Input data-testid="contato-input-email" type="email" required value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} className="mt-1"/>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">WhatsApp</Label>
        <Input data-testid="contato-input-whatsapp" required placeholder="(11) 9 9999-9999" value={form.whatsapp} onChange={(e)=>setForm({...form, whatsapp:e.target.value})} className="mt-1"/>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">Interesse</Label>
        <Select value={form.interest} onValueChange={(v)=>setForm({...form, interest:v})}>
          <SelectTrigger className="mt-1" data-testid="contato-input-interest"><SelectValue placeholder="Selecione"/></SelectTrigger>
          <SelectContent>
            {INTERESTS.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs uppercase tracking-wider font-semibold">Mensagem (opcional)</Label>
        <Textarea data-testid="contato-input-message" rows={4} value={form.message} onChange={(e)=>setForm({...form, message:e.target.value})} className="mt-1" placeholder="Conte um pouco sobre seu objetivo."/>
      </div>
    </div>

    <button type="submit" disabled={loading} className="mt-7 btn-primary" data-testid="contato-submit">
      {loading ? (<><Loader2 size={16} className="animate-spin"/> Enviando...</>) : (<>Enviar mensagem <ArrowRight size={16}/></>)}
    </button>

    <p className="mt-5 text-xs text-[#666] border-t border-[#E0E0E0] pt-4">
      Ao enviar, você concorda com o tratamento dos seus dados pela Clínica AEVOS conforme nossa Política de Privacidade (LGPD).
    </p>
  </form>
);

const Contato = () => {
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", interest: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads/contact`, form);
      setSent(true);
      pushGtmEvent("contact_form_submit", { interest: form.interest });
      toast.success("Mensagem enviada com sucesso.");
    } catch (err) {
      toast.error("Não foi possível enviar agora. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contato">
      <PageHero
        eyebrow="Contato & Agendamento"
        title="Vamos conversar antes de você decidir."
        subtitle="Envie sua mensagem ou fale diretamente com a clínica pelo WhatsApp. Atendimento presencial em Mogi das Cruzes e online para todo o Brasil."
      />

      <section className="section-pad">
        <div className="aevos-container grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-6">
            <ContactWhatsAppCard />
            <ContactAddressCard />
            <div className="p-5 border border-[#E0E0E0] bg-[#E6F8FF] text-xs text-[#333]">
              <strong className="text-black">Responsável técnico:</strong> Dr. Guilherme Affonso — CRM SP 200364 · RQE 120948 · Especialidade: Medicina Preventiva e Social.
            </div>
          </div>

          <div className="lg:col-span-3">
            {sent ? (
              <ContactSuccessCard firstName={form.name.split(" ")[0]} />
            ) : (
              <ContactFormCard form={form} setForm={setForm} loading={loading} onSubmit={submit} />
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contato;
